<template>
  <div
    v-loading="loading"
    class="protocol-page"
    element-loading-text="加载中..."
    element-loading-background="rgba(255, 255, 255, 0.8)"
  >
    <!-- ====== Hero ====== -->
    <div class="info-hero">
      <div class="hero-bg">
        <svg viewBox="0 0 800 200" preserveAspectRatio="none" class="hero-svg">
          <circle cx="100" cy="100" r="180" fill="rgba(255,255,255,0.05)" />
          <circle cx="650" cy="40" r="150" fill="rgba(8,145,178,0.22)" />
          <circle cx="780" cy="180" r="90" fill="rgba(255,255,255,0.06)" />
          <path
            d="M 0 150 Q 200 100 400 130 T 800 110"
            stroke="rgba(255,255,255,0.10)"
            stroke-width="1"
            fill="none"
          />
          <path
            d="M 0 170 Q 220 140 420 160 T 800 140"
            stroke="rgba(255,255,255,0.05)"
            stroke-width="1"
            fill="none"
          />
        </svg>
      </div>

      <button class="back-btn" @click="router.back()">
        <el-icon><ArrowLeft /></el-icon>
        <span>返回</span>
      </button>

      <div class="hero-main">
        <div class="hero-avatar">
          <el-icon><Files /></el-icon>
        </div>
        <div class="hero-text">
          <div class="hero-tag">SHIP PROTOCOL · 运营协议管理</div>
          <h1 class="hero-title">船舶运营协议管理</h1>
          <div v-if="currentShipName" class="hero-subtitle">
            目标船舶：{{ currentShipName }}
          </div>
        </div>
      </div>
    </div>

    <!-- ====== 统计卡片条 ====== -->
    <div class="stat-strip">
      <div class="stat-mini stat-total">
        <div class="stat-mini-icon">
          <el-icon><DataLine /></el-icon>
        </div>
        <div class="stat-mini-body">
          <div class="stat-mini-label">协议项总数</div>
          <div class="stat-mini-value">{{ tableData.length }}</div>
        </div>
      </div>
      <div class="stat-mini stat-completion">
        <div class="stat-mini-icon">
          <el-icon><Histogram /></el-icon>
        </div>
        <div class="stat-mini-body">
          <div class="stat-mini-label">完整度</div>
          <div class="stat-mini-value">
            {{ Math.round(completionRate * 100) }}%
          </div>
        </div>
        <div class="stat-mini-bar">
          <span
            class="stat-mini-bar-fill"
            :style="{ width: completionRate * 100 + '%' }"
          ></span>
        </div>
      </div>
      <div class="stat-mini stat-groups">
        <div class="stat-mini-icon">
          <el-icon><MagicStick /></el-icon>
        </div>
        <div class="stat-mini-body">
          <div class="stat-mini-label">已配置组</div>
          <div class="stat-mini-value">{{ groupsCompleted }} / 3</div>
        </div>
      </div>
      <div class="stat-mini stat-dirty">
        <div class="stat-mini-icon">
          <el-icon><Filter /></el-icon>
        </div>
        <div class="stat-mini-body">
          <div class="stat-mini-label">未保存修改</div>
          <div class="stat-mini-value">{{ dirtyCount }}</div>
        </div>
      </div>
    </div>
    <!-- 主内容 -->
    <div class="main-content">
      <!-- 操作工具栏（船舶已在 ShipInfoView 中选定，无需此处再选） -->
      <div class="toolbar-card">
        <div class="toolbar-search">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索字段名、数据名称或数据格式..."
            clearable
            :prefix-icon="Search"
            size="default"
          />
        </div>
        <div class="toolbar-actions">
          <el-button :icon="Refresh" class="btn-tool" @click="loadData">
            刷新
          </el-button>
          <el-upload
            accept=".xlsx"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleImportExcel"
          >
            <el-button :icon="Upload" class="btn-tool btn-warning">
              导入 Excel
            </el-button>
          </el-upload>
          <el-button
            :icon="Download"
            class="btn-tool btn-primary"
            @click="handleExportExcel"
          >
            导出 Excel
          </el-button>
          <el-button
            :icon="Check"
            class="btn-tool btn-primary"
            :disabled="!isDirty"
            @click="handleSaveAll"
          >
            保存全部
            <span v-if="isDirty" class="dirty-badge">{{ dirtyCount }}</span>
          </el-button>
          <el-button
            :icon="Promotion"
            class="btn-tool btn-success"
            :disabled="!selectedShipId"
            :loading="pushLoading"
            @click="handlePush"
          >
            推送到终端
          </el-button>
        </div>
      </div>

      <!-- 第 1 组：船舶基础数据 -->
      <div class="group-card group-card-base">
        <div class="group-header">
          <div class="group-icon-base">
            <el-icon :size="22"><Cellphone /></el-icon>
          </div>
          <div class="group-info">
            <div class="group-title-row">
              <span class="group-title">船舶基础数据</span>
              <el-tag type="primary" effect="dark" round>
                {{ getGroupData(1, 19).length }} 项
              </el-tag>
            </div>
            <div class="group-subtitle">
              序号 1-19 · 终端自身传感器数据（4G / GPS / GNSS）
            </div>
          </div>
          <el-button
            type="primary"
            :icon="Plus"
            size="small"
            @click="handleAddItem(1, 19)"
          >
            新增
          </el-button>
        </div>
        <el-table
          :data="getFilteredGroupData(1, 19)"
          border
          stripe
          :empty-text="
            searchKeyword ? '没有匹配的数据' : '暂无数据，点击「新增」添加'
          "
          class="protocol-table"
        >
          <el-table-column label="数据序号" width="100" align="center">
            <template #default="{ row }">
              <el-input
                v-model.number="row.dataId"
                type="number"
                size="small"
                :class="{
                  'data-id-input': true,
                  'cell-dirty': isFieldDirty(row, 'dataId', row.dataId),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="字段名称" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.fieldName"
                placeholder="驼峰命名，如 gprsRssi"
                size="small"
                :class="{
                  'has-error': !isValidFieldName(row.fieldName),
                  'cell-dirty': isFieldDirty(row, 'fieldName', row.fieldName),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="字段简写" min-width="100">
            <template #default="{ row }">
              <el-input
                v-model="row.shortCode"
                placeholder="如 g1/b2/c3"
                size="small"
                :class="{
                  'has-error': !isValidShortCode(row.shortCode),
                  'cell-dirty': isFieldDirty(row, 'shortCode', row.shortCode),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据名称" min-width="160">
            <template #default="{ row }">
              <el-input
                v-model="row.dataName"
                placeholder="中文描述"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataName', row.dataName),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据格式" min-width="180">
            <template #default="{ row }">
              <el-input
                v-model="row.dataFormat"
                placeholder="如 I::%:DATA"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataFormat', row.dataFormat),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据来源" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.dataSource"
                placeholder="如 电池域?:x3"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataSource', row.dataSource),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据说明" min-width="180">
            <template #default="{ row }">
              <el-input
                v-model="row.description"
                placeholder="说明"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(
                    row,
                    'description',
                    row.description
                  ),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="70" align="center" fixed="right">
            <template #default="{ row }">
              <!-- 默认数据：可恢复（有未保存修改或已保存专属副本时显示），不可删除 -->
              <el-tooltip
                v-if="isDefaultData(row) && canRestore(row)"
                content="恢复默认"
                placement="top"
              >
                <el-button
                  type="warning"
                  :icon="RefreshLeft"
                  size="small"
                  circle
                  @click="handleRestore(row)"
                />
              </el-tooltip>
              <!-- 新增数据：可删除 -->
              <el-tooltip
                v-else-if="!isDefaultData(row)"
                content="删除"
                placement="top"
              >
                <el-button
                  type="danger"
                  :icon="Delete"
                  size="small"
                  circle
                  @click="handleDelete(row)"
                />
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 第 2 组：电池运营数据 -->
      <div class="group-card group-card-warning">
        <div class="group-header">
          <div class="group-icon-warning">
            <el-icon :size="22"><Lightning /></el-icon>
          </div>
          <div class="group-info">
            <div class="group-title-row">
              <span class="group-title">电池运营数据</span>
              <el-tag type="warning" effect="dark" round>
                {{ getGroupData(20, 99).length }} 项
              </el-tag>
            </div>
            <div class="group-subtitle">
              序号 20-99 · 电池能耗 / SOC / 工况等数据
            </div>
          </div>
          <el-button
            type="warning"
            :icon="Plus"
            size="small"
            @click="handleAddItem(20, 99)"
          >
            新增
          </el-button>
        </div>
        <el-table
          :data="getFilteredGroupData(20, 99)"
          border
          stripe
          :empty-text="
            searchKeyword ? '没有匹配的数据' : '暂无数据，点击「新增」添加'
          "
          class="protocol-table"
        >
          <el-table-column label="数据序号" width="100" align="center">
            <template #default="{ row }">
              <el-input
                v-model.number="row.dataId"
                type="number"
                size="small"
                :class="{
                  'data-id-input': true,
                  'cell-dirty': isFieldDirty(row, 'dataId', row.dataId),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="字段名称" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.fieldName"
                placeholder="驼峰命名，如 gprsRssi"
                size="small"
                :class="{
                  'has-error': !isValidFieldName(row.fieldName),
                  'cell-dirty': isFieldDirty(row, 'fieldName', row.fieldName),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="字段简写" min-width="100">
            <template #default="{ row }">
              <el-input
                v-model="row.shortCode"
                placeholder="如 g1/b2/c3"
                size="small"
                :class="{
                  'has-error': !isValidShortCode(row.shortCode),
                  'cell-dirty': isFieldDirty(row, 'shortCode', row.shortCode),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据名称" min-width="160">
            <template #default="{ row }">
              <el-input
                v-model="row.dataName"
                placeholder="中文描述"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataName', row.dataName),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据格式" min-width="180">
            <template #default="{ row }">
              <el-input
                v-model="row.dataFormat"
                placeholder="如 I::%:DATA"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataFormat', row.dataFormat),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据来源" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.dataSource"
                placeholder="如 电池域?:x3"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataSource', row.dataSource),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据说明" min-width="180">
            <template #default="{ row }">
              <el-input
                v-model="row.description"
                placeholder="说明"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(
                    row,
                    'description',
                    row.description
                  ),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="70" align="center" fixed="right">
            <template #default="{ row }">
              <!-- 默认数据：可恢复（有未保存修改或已保存专属副本时显示），不可删除 -->
              <el-tooltip
                v-if="isDefaultData(row) && canRestore(row)"
                content="恢复默认"
                placement="top"
              >
                <el-button
                  type="warning"
                  :icon="RefreshLeft"
                  size="small"
                  circle
                  @click="handleRestore(row)"
                />
              </el-tooltip>
              <!-- 新增数据：可删除 -->
              <el-tooltip
                v-else-if="!isDefaultData(row)"
                content="删除"
                placement="top"
              >
                <el-button
                  type="danger"
                  :icon="Delete"
                  size="small"
                  circle
                  @click="handleDelete(row)"
                />
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 第 3 组：船舶运营数据 -->
      <div class="group-card group-card-success">
        <div class="group-header">
          <div class="group-icon-success">
            <el-icon :size="22"><TrendCharts /></el-icon>
          </div>
          <div class="group-info">
            <div class="group-title-row">
              <span class="group-title">船舶运营数据</span>
              <el-tag type="success" effect="dark" round>
                {{ getGroupData(100, 999).length }} 项
              </el-tag>
            </div>
            <div class="group-subtitle">
              序号 101-111 · 船舶综合数据（供 CCS 报送）
            </div>
          </div>
          <el-button
            type="success"
            :icon="Plus"
            size="small"
            @click="handleAddItem(100, 999)"
          >
            新增
          </el-button>
        </div>
        <el-table
          :data="getFilteredGroupData(100, 999)"
          border
          stripe
          :empty-text="
            searchKeyword ? '没有匹配的数据' : '暂无数据，点击「新增」添加'
          "
          class="protocol-table"
        >
          <el-table-column label="数据序号" width="100" align="center">
            <template #default="{ row }">
              <el-input
                v-model.number="row.dataId"
                type="number"
                size="small"
                class="data-id-input"
              />
            </template>
          </el-table-column>
          <el-table-column label="字段名称" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.fieldName"
                placeholder="驼峰命名，如 gprsRssi"
                size="small"
                :class="{
                  'has-error': !isValidFieldName(row.fieldName),
                  'cell-dirty': isFieldDirty(row, 'fieldName', row.fieldName),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="字段简写" min-width="100">
            <template #default="{ row }">
              <el-input
                v-model="row.shortCode"
                placeholder="如 g1/b2/c3"
                size="small"
                :class="{
                  'has-error': !isValidShortCode(row.shortCode),
                  'cell-dirty': isFieldDirty(row, 'shortCode', row.shortCode),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据名称" min-width="160">
            <template #default="{ row }">
              <el-input
                v-model="row.dataName"
                placeholder="中文描述"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataName', row.dataName),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据格式" min-width="180">
            <template #default="{ row }">
              <el-input
                v-model="row.dataFormat"
                placeholder="如 D:6:deg:DATA"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataFormat', row.dataFormat),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据来源" min-width="200">
            <template #default="{ row }">
              <el-input
                v-model="row.dataSource"
                placeholder="空字符串"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(row, 'dataSource', row.dataSource),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="数据说明" min-width="180">
            <template #default="{ row }">
              <el-input
                v-model="row.description"
                placeholder="说明"
                size="small"
                :class="{
                  'cell-dirty': isFieldDirty(
                    row,
                    'description',
                    row.description
                  ),
                }"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="70" align="center" fixed="right">
            <template #default="{ row }">
              <!-- 默认数据：可恢复（有未保存修改或已保存专属副本时显示），不可删除 -->
              <el-tooltip
                v-if="isDefaultData(row) && canRestore(row)"
                content="恢复默认"
                placement="top"
              >
                <el-button
                  type="warning"
                  :icon="RefreshLeft"
                  size="small"
                  circle
                  @click="handleRestore(row)"
                />
              </el-tooltip>
              <!-- 新增数据：可删除 -->
              <el-tooltip
                v-else-if="!isDefaultData(row)"
                content="删除"
                placement="top"
              >
                <el-button
                  type="danger"
                  :icon="Delete"
                  size="small"
                  circle
                  @click="handleDelete(row)"
                />
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="bottom-spacer"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import router from "@/router";
import { ElMessage, ElMessageBox } from "element-plus";
import type { UploadFile } from "element-plus";
import {
  DataLine,
  Ship,
  Histogram,
  MagicStick,
  Files,
  Refresh,
  Upload,
  Download,
  Check,
  Promotion,
  Plus,
  Delete,
  RefreshLeft,
  Search,
  Filter,
  InfoFilled,
  CopyDocument,
  ArrowLeft,
} from "@element-plus/icons-vue";
import {
  getProtocolListApi,
  saveProtocolApi,
  deleteProtocolItemApi,
  pushProtocolApi,
  type ShipProtocolItem,
} from "@/api/operation/shipProtocolTemplateApi";
import { fetchOrganizationShipListApi } from "@/api/productApi";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { loadProtocolText } from "@/composables/useProtocolText";
import {
  translateSrcToZh,
  translateSrcToEn,
  loadTranslations,
} from "@/composables/useTranslation";

const route = useRoute();
const selectedShipId = ref<string>((route.query.shipId as string) || "");
// 船舶名优先从路由 query 中取（跳转即带），shipList 异步加载后再覆盖一次
const cachedShipName = (route.query.shipName as string) || "";
const tableData = ref<ShipProtocolItem[]>([]);
const originalData = ref<ShipProtocolItem[]>([]);
// 全局默认模板（dataId -> 默认项），船舶视图用于判定"默认数据"及恢复基准
const defaultItemsMap = ref<Map<number, ShipProtocolItem>>(new Map());
const shipList = ref<{ id: string; name: string }[]>([]);
const pushLoading = ref(false);
const loading = ref(false);
const searchKeyword = ref<string>("");

const currentShipName = computed(() => {
  if (!selectedShipId.value) return "全局模板";
  const ship = shipList.value.find((s) => s.id === selectedShipId.value);
  return ship?.name || cachedShipName || "未知船舶";
});

const getGroupData = (min: number, max: number): ShipProtocolItem[] => {
  return tableData.value.filter(
    (item) => item.dataId >= min && item.dataId <= max
  );
};

const getFilteredGroupData = (min: number, max: number): ShipProtocolItem[] => {
  const data = tableData.value.filter((item) => {
    // 已保存项：用 originalData 中的原始 dataId 判断归属组
    if (item.id) {
      const orig = originalData.value.find((o) => o.id === item.id);
      const origDataId = orig ? orig.dataId : item.dataId;
      return origDataId >= min && origDataId <= max;
    }
    // 新增项：用 sortOrder 判断归属组
    if (item.sortOrder === min) return true;
    // 导入项（id 为空但 sortOrder 不是 min）：按 dataId 判断
    if (item.dataId >= min && item.dataId <= max) return true;
    return false;
  });
  if (!searchKeyword.value) return data;
  const kw = searchKeyword.value.toLowerCase();
  return data.filter(
    (item) =>
      (item.fieldName && item.fieldName.toLowerCase().includes(kw)) ||
      (item.dataName && item.dataName.toLowerCase().includes(kw)) ||
      (item.dataFormat && item.dataFormat.toLowerCase().includes(kw))
  );
};

const isValidFieldName = (name: string): boolean => {
  if (!name) return true;
  return /^[a-z][a-zA-Z0-9]*$/.test(name);
};

/**
 * 字段简写（shortCode）格式校验：允许为空（保存时后端按 dataId 推导），
 * 非空时须为小写字母开头 + 小写字母/数字（如 g1/b2/c10/d30）
 */
const isValidShortCode = (code: string): boolean => {
  if (!code) return true;
  return /^[a-z][a-z0-9]*$/.test(code);
};

/**
 * 按数据序号推导字段简写默认值（与后端 deriveShortCode 规则一致）：
 * 1-9 -> g{n}，20 -> m1，21-26 -> b{n-20}，101-111 -> c{n-100}，其他 -> d{n}
 * 新增行时预填，用户可改；保存时后端同样规则兜底
 */
const deriveShortCode = (dataId: number): string => {
  if (dataId >= 1 && dataId <= 9) return `g${dataId}`;
  if (dataId === 20) return "m1";
  if (dataId >= 21 && dataId <= 26) return `b${dataId - 20}`;
  if (dataId >= 101 && dataId <= 111) return `c${dataId - 100}`;
  return `d${dataId}`;
};

const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success(`已复制: ${text}`);
  } catch {
    ElMessage.warning("复制失败，请手动复制");
  }
};

const groupsCompleted = computed(() => {
  let count = 0;
  if (getGroupData(1, 9).length > 0) count++;
  if (getGroupData(20, 26).length > 0) count++;
  if (getGroupData(100, 999).length > 0) count++;
  return count;
});

const completionRate = computed(() => {
  const total = 27; // 9+7+11
  return Math.min(tableData.value.length / total, 1);
});

const isDirty = computed(() => dirtyCount.value > 0);
const dirtyCount = computed(() => {
  return tableData.value.filter((item) => isItemDirty(item)).length;
});

const isItemDirty = (item: ShipProtocolItem): boolean => {
  if (!item.id) return true;
  const orig = originalData.value.find((o) => o.id === item.id);
  if (!orig) return true;
  return (
    orig.dataId !== item.dataId ||
    orig.fieldName !== item.fieldName ||
    orig.shortCode !== item.shortCode ||
    orig.dataName !== item.dataName ||
    orig.dataFormat !== item.dataFormat ||
    orig.dataSource !== item.dataSource ||
    orig.description !== item.description
  );
};

/**
 * 检查单项中某字段是否被修改过
 * @param item 当前项
 * @param field 字段名
 * @param currentValue 当前输入框的值
 * @returns 是否被修改
 */
const isFieldDirty = (
  item: ShipProtocolItem,
  field: keyof ShipProtocolItem,
  currentValue: any
): boolean => {
  if (!item.id) {
    // 新增项：只要该字段有值就标记为已修改
    return (
      currentValue !== "" && currentValue !== null && currentValue !== undefined
    );
  }
  const orig = originalData.value.find((o) => o.id === item.id);
  if (!orig) return true;
  return (orig as any)[field] !== currentValue;
};

const loadShipList = async () => {
  const res = await fetchOrganizationShipListApi("");
  if (res) {
    shipList.value = res.map((s: any) => ({ id: s.id, name: s.name }));
  }
};

const loadData = async () => {
  loading.value = true;
  try {
    // 翻译缓存就绪再做英文->中文转换（幂等共享；登录后首次进入时 App/登录页阶段的
    // 加载可能因 401 失败导致缓存为空，此处在每个 loadData 入口兜底重试，避免显示英文）
    await loadTranslations();
    // 船舶视图：同时加载全局默认模板，用于判定"默认数据"与恢复基准
    const [res, defaults] = await Promise.all([
      getProtocolListApi(selectedShipId.value || undefined),
      selectedShipId.value ? getProtocolListApi() : Promise.resolve(null),
    ]);
    defaultItemsMap.value = new Map((defaults || []).map((d) => [d.dataId, d]));
    if (res) {
      // 页面展示/编辑统一中文设备名：库存英文 src -> 前端翻译缓存转中文
      // （保存提交前 translateSrcToEn 转回英文，库内 data_source 始终为英文）
      res.forEach((item: ShipProtocolItem) => {
        item.dataSource = translateSrcToZh(item.dataSource);
      });
      tableData.value = res;
      originalData.value = JSON.parse(JSON.stringify(res));
    } else {
      tableData.value = [];
      originalData.value = [];
    }
  } finally {
    loading.value = false;
  }
};

/**
 * 判断某行是否为默认模板数据（船舶视图下，该数据序号存在于全局默认模板中）
 * 默认数据不可删除，只能"恢复默认"
 */
const isDefaultData = (row: ShipProtocolItem): boolean => {
  // 未保存的新增行（含 Excel 导入的新增行）一律按"新增数据"处理，可删除、不可恢复：
  // 即使用户把数据序号改成与默认模板相同的值，也不能变成"恢复默认"，
  // 否则会出现两条同序号行、恢复逻辑错乱
  if (!row.id) return false;
  return !!selectedShipId.value && defaultItemsMap.value.has(row.dataId);
};

/**
 * 判断某行是否为已保存的船舶专属副本（覆盖了默认模板的修改）
 */
const isShipOverride = (row: ShipProtocolItem): boolean => {
  return !!row.id && !!row.shipId && row.shipId === selectedShipId.value;
};

/**
 * 默认数据是否可恢复：有未保存修改，或已保存为船舶专属副本
 */
const canRestore = (row: ShipProtocolItem): boolean => {
  return isItemDirty(row) || isShipOverride(row);
};

/**
 * 恢复默认数据（立即生效，无需再点保存）：
 * - 已保存的专属副本：弹窗确认后删除数据库中的副本，并重载列表
 * - 未保存的修改：纯本地恢复为默认模板值（不弹窗、不请求），同步基准快照（清除脏标记），
 *   同时保留页面上其他行的未保存修改
 */
const handleRestore = async (row: ShipProtocolItem) => {
  // 防御：只有当前行的 shipId 等于当前船舶 ID（即确为已保存的船舶专属副本）才允许调删除接口，
  // 避免状态错乱时误删默认模板数据
  const isOverride =
    !!row.id &&
    !!row.shipId &&
    row.shipId === selectedShipId.value &&
    !!selectedShipId.value;
  if (isOverride) {
    // 已保存的专属副本：删除会影响数据库，需弹窗确认
    const displayName = row.dataName || row.dataId;
    try {
      await ElMessageBox.confirm(
        `确认将「${displayName}」恢复为默认数据？该船舶的专属修改将被删除。`,
        "恢复确认",
        {
          type: "warning",
          confirmButtonText: "确认恢复",
          cancelButtonText: "取消",
        }
      );
    } catch {
      // 用户取消
      return;
    }
    const res = await deleteProtocolItemApi(row.id);
    if (!res) {
      ElMessage.error("恢复失败");
      return;
    }
  }
  const source = defaultItemsMap.value.get(row.dataId);
  if (source) {
    const copy = JSON.parse(JSON.stringify(source));
    // 默认模板库存英文 src，恢复到页面时同步转中文（保持展示/编辑态一致）
    copy.dataSource = translateSrcToZh(copy.dataSource);
    Object.assign(row, copy);
    // 同步基准快照（按 dataId 定位），保证 isItemDirty 判定正确、恢复按钮随之消失；
    // 找不到基准时补插一份，避免行被持续误判为"有修改"
    const origIndex = originalData.value.findIndex(
      (o) => o.dataId === row.dataId
    );
    if (origIndex > -1) {
      originalData.value[origIndex] = JSON.parse(JSON.stringify(copy));
    } else {
      originalData.value.push(JSON.parse(JSON.stringify(copy)));
    }
  }
  ElMessage.success("已恢复为默认数据");
  if (isOverride) {
    // 已保存场景：数据库已变更，重新加载列表由后端合并结果重建行状态，
    // 确保恢复按钮必然消失（本地对象同步在任何异常路径下都可能残留按钮）
    await loadData();
    // 协议 TEXT 枚举可能已变更，刷新前端缓存
    loadProtocolText();
  }
};

watch(selectedShipId, () => {
  loadData();
});

const handleSaveAll = async () => {
  if (!isDirty.value) {
    ElMessage.info("没有修改需要保存");
    return;
  }
  // 保存前校验：数据序号不能为空、不能重复（后端主键由序号生成，重复会导致保存失败）
  const seenIds = new Set<number>();
  const dupIds = new Set<number>();
  let hasEmptyId = false;
  for (const item of tableData.value) {
    if (item.dataId == null) {
      hasEmptyId = true;
      continue;
    }
    if (seenIds.has(item.dataId)) dupIds.add(item.dataId);
    else seenIds.add(item.dataId);
  }
  if (hasEmptyId) {
    ElMessage.error("存在未填写数据序号的行，请补全后再保存");
    return;
  }
  if (dupIds.size > 0) {
    const sortedIds = [...dupIds].sort((a, b) => a - b).join("、");
    ElMessage.error(`数据序号重复：${sortedIds}，请修改后再保存`);
    return;
  }
  // 保存前校验：字段简写格式（空=后端推导；非空须小写字母开头）与重复
  // （简写是 [2,5] 下发 / [3,2] 上报的配对键，重复会导致解析歧义，后端同样校验）
  const badCodes = tableData.value.filter(
    (item) => !isValidShortCode(item.shortCode || "")
  );
  if (badCodes.length > 0) {
    const names = badCodes.map((i) => i.fieldName || i.dataId).join("、");
    ElMessage.error(
      `字段简写格式不正确（须小写字母开头，如 g1/b2/c3）：${names}`
    );
    return;
  }
  const seenCodes = new Set<string>();
  const dupCodes = new Set<string>();
  for (const item of tableData.value) {
    const code = (item.shortCode || "").trim();
    if (!code) continue; // 空值由后端按 dataId 推导（dataId 已查重）
    if (seenCodes.has(code)) dupCodes.add(code);
    else seenCodes.add(code);
  }
  if (dupCodes.size > 0) {
    ElMessage.error(
      `字段简写重复：${[...dupCodes].sort().join("、")}，请修改后再保存`
    );
    return;
  }
  const res = await saveProtocolApi(
    selectedShipId.value || null,
    // 提交前中文设备名转英文（页面编辑态为中文）：
    // 翻译缓存未命中的中文原样提交，后端保存入口 normalizeSrc 兜底翻译
    tableData.value.map((item) => ({
      ...item,
      dataSource: translateSrcToEn(item.dataSource),
    }))
  );
  if (res) {
    ElMessage.success("保存成功");
    // 后端保存时可能新翻译设备名并入库，强制刷新翻译缓存再重载
    // （否则 loadData 的英文->中文转换使用旧缓存，新设备名会显示英文）
    await loadTranslations(true);
    loadData();
    // 协议 TEXT 枚举可能已变更，刷新前端缓存
    loadProtocolText();
  } else {
    ElMessage.error("保存失败");
  }
};

const handleDelete = async (row: ShipProtocolItem) => {
  // 安全防线：船舶视图下的默认模板数据禁止删除（只能"恢复默认"），
  // 防止任何状态错乱导致默认数据被误删
  if (isDefaultData(row)) {
    ElMessage.warning("默认数据不可删除，如需撤销修改请使用「恢复默认」");
    return;
  }
  // 未保存的新增行：尚未入库，直接从列表移除，无需确认
  if (!row.id) {
    const index = tableData.value.findIndex((item) => item === row);
    if (index > -1) {
      tableData.value.splice(index, 1);
    }
    ElMessage.success("已删除");
    return;
  }
  try {
    await ElMessageBox.confirm(
      `确认删除「${row.dataName || row.dataId}」？此操作不可撤销。`,
      "删除确认",
      {
        type: "warning",
        confirmButtonText: "确认删除",
        cancelButtonText: "取消",
      }
    );
    const res = await deleteProtocolItemApi(row.id);
    if (!res) {
      ElMessage.error("删除失败");
      return;
    }
    const index = tableData.value.findIndex((item) => item === row);
    if (index > -1) {
      tableData.value.splice(index, 1);
      originalData.value = originalData.value.filter((o) => o.id !== row.id);
    }
    ElMessage.success("删除成功");
    // 协议 TEXT 枚举可能已变更，刷新前端缓存
    loadProtocolText();
  } catch {
    // 用户取消
  }
};

const handleAddItem = (min: number, max: number) => {
  // 已被占用的数据序号集合（全局统计，含此前新增或超出组范围的行，如第 1 组的 10）
  const usedIds = new Set(
    tableData.value
      .map((item) => item.dataId)
      .filter((id): id is number => id != null)
  );
  // 从组起始序号起取第一个未占用的序号
  // （旧逻辑按"组内最大序号+1"取号，组范围外（>max）的行会被过滤条件漏算，
  //   连续两次新增会拿到同一序号，保存时主键冲突 Duplicate entry）
  let newDataId = min;
  while (usedIds.has(newDataId)) newDataId++;
  tableData.value.push({
    id: "",
    shipId: selectedShipId.value || null,
    dataId: newDataId,
    fieldName: "",
    // 预填推导短码（用户可改，保存时空值由后端同规则兜底）
    shortCode: deriveShortCode(newDataId),
    dataName: "",
    dataFormat: "",
    dataSource: "",
    description: "",
    chTable: "",
    chColumn: "",
    // sortOrder 固定为 min，确保归入正确的组
    sortOrder: min,
  });
  ElMessage.success(
    `已新增数据项（序号 ${newDataId}），请填写字段信息后点击「保存全部」`
  );
};

const handlePush = async () => {
  if (!selectedShipId.value) {
    ElMessage.warning("请先选择船舶");
    return;
  }
  try {
    await ElMessageBox.confirm("确认推送协议到终端？", "推送确认", {
      type: "warning",
      confirmButtonText: "确认",
      cancelButtonText: "取消",
    });
    pushLoading.value = true;
    const res = await pushProtocolApi(selectedShipId.value);
    if (res) {
      if (res.success) {
        ElMessage.success(res.message || "推送成功");
      } else {
        ElMessage.error(res.message || "推送失败");
      }
    } else {
      ElMessage.error("推送失败");
    }
  } catch {
    // 用户取消
  } finally {
    pushLoading.value = false;
  }
};

const handleImportExcel = async (file: UploadFile) => {
  if (!file.raw) return;
  try {
    // 翻译缓存就绪（下方解析时 dataSource 英->中转换依赖缓存；幂等共享，
    // App 阶段加载 401 失败时此处重试兜底）
    await loadTranslations();
    const buffer = await file.raw.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const worksheet = workbook.getWorksheet(1);
    if (!worksheet) {
      ElMessage.error("Excel 中未找到工作表");
      return;
    }
    const rows: ShipProtocolItem[] = [];
    // 全部列标题（含新模板的"字段简写"列）
    const headerTexts = [
      "数据序号",
      "字段名称",
      "字段简写",
      "数据名称",
      "数据格式",
      "数据来源",
      "数据说明",
    ];
    // 定义分组标题（用于跳过）
    const groupTitles = ["船舶基础数据", "电池运营数据", "船舶运营数据"];
    // 列标题 -> 列索引映射：遇到表头行动态重建，兼容无"字段简写"列的旧模板
    let colMap: Record<string, number> = {};

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // 跳过第1行（分组标题或表头）
      const values = row.values as any[];
      // ExcelJS 的 values 数组从索引 1 开始
      const cellA = values[1]?.toString()?.trim() || "";

      // 跳过空行
      if (!cellA) return;
      // 表头行：重建列映射（每组数据前各有一行表头）
      if (cellA === "数据序号") {
        colMap = {};
        values.forEach((v, idx) => {
          const t = v?.toString()?.trim();
          if (t && headerTexts.includes(t)) colMap[t] = idx;
        });
        return;
      }
      // 跳过其余表头文本行与分组标题行（历史结构防御）
      if (headerTexts.includes(cellA) || groupTitles.includes(cellA)) return;

      const dataId = parseInt(cellA, 10);
      if (isNaN(dataId)) return;

      // 按列映射取值；旧模板无"字段简写"列时取空串（保存时后端按 dataId 推导）
      const col = (title: string): string =>
        colMap[title] != null
          ? (values[colMap[title]]?.toString() || "").trim()
          : "";

      rows.push({
        id: "",
        shipId: selectedShipId.value || null,
        dataId,
        fieldName: col("字段名称"),
        shortCode: col("字段简写"),
        dataName: col("数据名称"),
        dataFormat: col("数据格式"),
        // 数据来源统一中文态进表格（Excel 可能填中文或英文，英文经翻译转中文；
        // 保存提交前 translateSrcToEn 统一转英文入库，导出亦转中文）
        dataSource: translateSrcToZh(col("数据来源")),
        description: col("数据说明"),
        chTable: "",
        chColumn: "",
        sortOrder: dataId,
      });
    });

    if (rows.length === 0) {
      ElMessage.warning("未解析到有效数据");
      return;
    }

    // 校验数据序号重复（保存时后端按序号生成主键，重复必然失败，提前在导入时拦截）
    const seenIds = new Set<number>();
    const dupIds = new Set<number>();
    for (const r of rows) {
      if (seenIds.has(r.dataId)) dupIds.add(r.dataId);
      else seenIds.add(r.dataId);
    }
    if (dupIds.size > 0) {
      ElMessage.error(
        `Excel 中数据序号重复：${[...dupIds]
          .sort((a, b) => a - b)
          .join("、")}，请修正后重新导入`
      );
      return;
    }

    // 请求数据库最新数据，与 Excel 数据对比
    const dbRes = await getProtocolListApi(selectedShipId.value || undefined);
    if (dbRes && dbRes.length > 0) {
      // 库存英文 src 转中文（Excel 侧已在解析时统一中文态，中文 vs 中文可比；
      // 否则任一侧为英文都会恒不等，导入对比会误判为"已修改"）
      dbRes.forEach((item: ShipProtocolItem) => {
        item.dataSource = translateSrcToZh(item.dataSource);
      });
      // 把 Excel 数据和数据库数据按 dataId 匹配
      // 匹配到：完整继承数据库行的 id/shipId/chTable/chColumn/sortOrder ——
      //   - id/shipId 决定"默认数据 vs 船舶专属副本"的判定（影响恢复/删除按钮）
      //   - chTable/chColumn 是 ClickHouse 取数映射，不继承会在保存时被空串覆盖，导致 CCS 报表取数失效
      // 未匹配到（新增）：id 为空，标记为新增
      for (const excelRow of rows) {
        const dbItem = dbRes.find((d) => d.dataId === excelRow.dataId);
        if (dbItem) {
          excelRow.id = dbItem.id;
          excelRow.shipId = dbItem.shipId;
          excelRow.chTable = dbItem.chTable || "";
          excelRow.chColumn = dbItem.chColumn || "";
          excelRow.sortOrder = dbItem.sortOrder ?? dbItem.dataId;
        }
      }
      // originalData 设为数据库数据，这样只有真正改了的字段才会高亮
      originalData.value = JSON.parse(JSON.stringify(dbRes));
    } else {
      // 数据库没有数据，全部视为新增
      originalData.value = JSON.parse(JSON.stringify(rows));
    }

    tableData.value = rows;

    // 统计差异
    const dirtyCount = rows.filter((item) => isItemDirty(item)).length;
    const totalCount = rows.length;
    if (dirtyCount === 0) {
      ElMessage.success(
        `导入成功，共 ${totalCount} 条数据，与数据库一致，无需保存`
      );
    } else {
      ElMessage.success(
        `导入成功，共 ${totalCount} 条数据，其中 ${dirtyCount} 条有修改`
      );
    }
  } catch (e: any) {
    const msg = e.message || "";
    if (msg.includes("zip") || msg.includes("central directory")) {
      ElMessage.error(
        "导入失败：文件格式不支持。请确保使用 .xlsx 格式（不要用 .xls 或其他格式另存）"
      );
    } else {
      ElMessage.error("导入失败: " + msg);
    }
  }
};

const handleExportExcel = () => {
  if (tableData.value.length === 0) {
    ElMessage.warning("没有数据可导出");
    return;
  }
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("协议数据");
  const groups = [
    { name: "船舶基础数据", min: 1, max: 19, color: "FF409EFF" },
    { name: "电池运营数据", min: 20, max: 99, color: "FFE6A23C" },
    { name: "船舶运营数据", min: 100, max: 999, color: "FF67C23A" },
  ];
  const columns = [
    { key: "dataId", header: "数据序号", width: 10 },
    { key: "fieldName", header: "字段名称", width: 25 },
    { key: "shortCode", header: "字段简写", width: 10 },
    { key: "dataName", header: "数据名称", width: 25 },
    { key: "dataFormat", header: "数据格式", width: 35 },
    { key: "dataSource", header: "数据来源", width: 25 },
    { key: "description", header: "数据说明", width: 35 },
  ];
  worksheet.columns = columns;
  let currentRow = 1;
  const centerStyle = {
    vertical: "middle" as const,
    horizontal: "center" as const,
    wrapText: true,
  };
  for (const group of groups) {
    const groupData = tableData.value.filter(
      (item) => item.dataId >= group.min && item.dataId <= group.max
    );
    if (groupData.length === 0) continue;
    const groupRow = worksheet.addRow({
      dataId: group.name,
      fieldName: "",
      shortCode: "",
      dataName: "",
      dataFormat: "",
      dataSource: "",
      description: "",
    });
    worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
    groupRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      cell.alignment = centerStyle;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: group.color },
      };
    });
    groupRow.height = 24;
    currentRow++;
    const headerRow = worksheet.addRow(
      columns.reduce((acc, col) => {
        acc[col.key] = col.header;
        return acc;
      }, {} as any)
    );
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = centerStyle;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF5F7FA" },
      };
    });
    currentRow++;
    for (const item of groupData) {
      const row = worksheet.addRow({
        dataId: item.dataId,
        fieldName: item.fieldName || "",
        shortCode: item.shortCode || "",
        dataName: item.dataName || "",
        dataFormat: item.dataFormat || "",
        // 数据来源导出统一中文（表格值可能仍为英文——缓存未命中等场景，兜底转换；
        // 已是中文则 translateSrcToZh 原样返回）
        dataSource: translateSrcToZh(item.dataSource) || "",
        description: item.description || "",
      });
      row.eachCell((cell) => {
        cell.alignment = centerStyle;
      });
      currentRow++;
    }
    worksheet.addRow({});
    currentRow++;
  }
  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const date = new Date().toISOString().slice(0, 10);
    saveAs(blob, `船舶运营协议_${date}.xlsx`);
    ElMessage.success("导出成功");
  });
};

onMounted(async () => {
  // 等翻译缓存就绪再 loadData（src 英->中转换依赖缓存；与 App 启动加载共享同一次请求）
  await loadTranslations();
  // 先加载船舶列表，让船舶名计算属性可以正确解析
  await loadShipList();
  loadData();
});
</script>

<style lang="less" scoped>
.protocol-page {
  display: block;
  padding: 24px 24px 40px;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f4f8fb 0%, #e7eef5 100%);
}

.protocol-page > * + * {
  margin-top: 16px;
}

.main-content {
  max-width: 1600px;
  margin: 0 auto;
}

.bottom-spacer {
  height: 40px;
}

// ======================== Hero ========================
.info-hero {
  position: relative;
  background: linear-gradient(135deg, #0c2461 0%, #1e3799 45%, #0a3d62 100%);
  color: #fff;
  border-radius: 20px;
  padding: 28px 36px;
  overflow: hidden;
  box-shadow: 0 16px 36px rgba(12, 36, 97, 0.32);
}
.info-hero .hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.info-hero .hero-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.info-hero .back-btn {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  color: rgba(224, 242, 254, 0.9);
  font-size: 12px;
  cursor: pointer;
  backdrop-filter: blur(8px);
  margin-bottom: 14px;
  transition: background 0.2s ease, transform 0.2s ease;
}
.info-hero .back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-2px);
}
.info-hero .back-btn .el-icon {
  font-size: 13px;
}
.info-hero .hero-main {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 20px;
}
.info-hero .hero-avatar {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #e0f2fe 100%);
  color: #0c2461;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}
.info-hero .hero-avatar .el-icon {
  font-size: 32px;
  color: #0c2461;
  display: flex;
  align-items: center;
  justify-content: center;
}
.info-hero .hero-avatar .el-icon svg {
  width: 1em;
  height: 1em;
  fill: currentColor;
  stroke: currentColor;
}
.info-hero .hero-text {
  flex: 1;
}
.info-hero .hero-tag {
  display: inline-block;
  padding: 3px 10px;
  font-size: 10px;
  letter-spacing: 3px;
  font-weight: 600;
  color: #a5f3fc;
  background: rgba(165, 243, 252, 0.14);
  border: 1px solid rgba(165, 243, 252, 0.32);
  border-radius: 999px;
  margin-bottom: 8px;
}
.info-hero .hero-title {
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 6px;
  letter-spacing: 0.8px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  line-height: 1.2;
}
.info-hero .hero-subtitle {
  color: rgba(207, 250, 254, 0.85);
  font-size: 13px;
  margin: 0;
}

// ======================== 统计条 ========================
.stat-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}
.stat-mini {
  position: relative;
  background: #fff;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid rgba(8, 145, 178, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.stat-mini::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: currentColor;
  opacity: 0.85;
}
.stat-mini:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
}
.stat-mini.stat-total {
  color: #1e3799;
}
.stat-mini.stat-completion {
  color: #0891b2;
}
.stat-mini.stat-groups {
  color: #b45309;
}
.stat-mini.stat-dirty {
  color: #b91c1c;
}
.stat-mini-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  color: #fff;
}
.stat-mini.stat-total .stat-mini-icon {
  background: linear-gradient(135deg, #1e3799 0%, #1e40af 100%);
}
.stat-mini.stat-completion .stat-mini-icon {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
}
.stat-mini.stat-groups .stat-mini-icon {
  background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
}
.stat-mini.stat-dirty .stat-mini-icon {
  background: linear-gradient(135deg, #b91c1c 0%, #ef4444 100%);
}
.stat-mini-icon::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.25) 0%,
    transparent 100%
  );
  pointer-events: none;
}
.stat-mini-icon .el-icon {
  position: relative;
  z-index: 1;
  font-size: 22px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}
.stat-mini-icon .el-icon svg {
  width: 22px;
  height: 22px;
  fill: currentColor;
}
.stat-mini-body {
  flex: 1;
  min-width: 0;
}
.stat-mini-label {
  font-size: 12px;
  color: #64748b;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
}
.stat-mini-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.stat-mini-progress {
  margin-top: 6px;
  height: 4px;
  background: rgba(8, 145, 178, 0.12);
  border-radius: 2px;
  overflow: hidden;
}
.stat-mini-progress-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #0891b2 0%, #67e8f9 100%);
  border-radius: 2px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

// ======================== 工具栏 ========================
.toolbar-card {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(8, 145, 178, 0.12);
  border-radius: 14px;
  padding: 14px 18px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 14px rgba(8, 145, 178, 0.08);
  display: flex;
  gap: 16px;
  align-items: center;
}
.toolbar-search {
  flex: 0 0 320px;
}
.toolbar-search :deep(.el-input__wrapper) {
  border-radius: 8px;
  box-shadow: 0 0 0 1px rgba(8, 145, 178, 0.18);
  transition: box-shadow 0.2s ease;
}
.toolbar-search :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #0891b2, 0 0 0 4px rgba(8, 145, 178, 0.15);
}
.toolbar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.btn-tool {
  border-radius: 8px;
  font-weight: 600;
  border: none;
}
.btn-tool:hover {
  transform: translateY(-1px);
}
.btn-tool.btn-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #b45309 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.28);
}
.btn-tool.btn-warning:hover {
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.36);
}
.btn-tool.btn-primary {
  background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.28);
}
.btn-tool.btn-primary:hover {
  box-shadow: 0 6px 16px rgba(8, 145, 178, 0.36);
}
.btn-tool.btn-primary:disabled {
  background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
  box-shadow: none;
  color: #f1f5f9;
}
.btn-tool.btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.28);
}
.btn-tool.btn-success:hover {
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.36);
}
.btn-tool.btn-success:disabled {
  background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
  box-shadow: none;
  color: #f1f5f9;
}
.dirty-badge {
  display: inline-block;
  margin-left: 6px;
  min-width: 20px;
  height: 18px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.25);
  font-size: 11px;
  font-weight: 700;
}

@media (max-width: 1100px) {
  .stat-strip {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  .stat-strip {
    grid-template-columns: 1fr;
  }
  .toolbar-card {
    flex-direction: column;
    align-items: stretch;
  }
  .toolbar-search {
    flex: 1 1 auto;
  }
}
.ship-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 12px;
  padding: 2px 10px;
  font-size: 13px;
  font-weight: 500;
  color: #409eff;
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 12px;
}
.info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 24px;
  padding: 14px 20px;
  font-size: 13px;
  line-height: 1.6;
  color: #303133;
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f1ff 100%);
  border: 1px solid #d1e4ff;
  border-left: 5px solid #409eff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}
.banner-icon {
  color: #409eff;
  flex-shrink: 0;
}
.banner-main strong {
  color: #409eff;
  font-weight: 700;
  font-size: 14px;
  padding: 0 2px;
}
.toolbar-search {
  width: 220px;
  margin-right: 8px;
}

// ======================== 工具栏 ========================
.toolbar-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 360px;
}
.toolbar-icon {
  color: #909399;
  font-size: 16px;
}
.ship-select {
  width: 280px;
}
.toolbar-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.dirty-badge {
  display: inline-block;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 6px;
  margin-left: 6px;
  background: #f56c6c;
  color: #fff;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
}

// ======================== 分组卡片 ========================
.group-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  transition: box-shadow 0.3s, transform 0.3s;
}
.group-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}
.group-card-base {
  border-top: 3px solid #409eff;
}
.group-card-warning {
  border-top: 3px solid #e6a23c;
}
.group-card-success {
  border-top: 3px solid #67c23a;
}
.group-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.01) 0%,
    rgba(0, 0, 0, 0.03) 100%
  );
  border-bottom: 1px solid #ebeef5;
}
.group-icon-base,
.group-icon-warning,
.group-icon-success {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.group-icon-base {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}
.group-icon-warning {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}
.group-icon-success {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}
.group-info {
  flex: 1;
  min-width: 0;
}
.group-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}
.group-title {
  font-size: 17px;
  font-weight: 700;
  color: #303133;
}
.group-subtitle {
  font-size: 12px;
  color: #909399;
}

// ======================== 表格 ========================
.protocol-table {
  :deep(.el-table__header-wrapper) {
    background-color: #fafbfc;
  }
  :deep(th.el-table__cell) {
    background-color: #fafbfc !important;
    color: #606266;
    font-weight: 600;
    font-size: 13px;
  }
  :deep(.el-table__row) {
    transition: background-color 0.2s;
  }
  :deep(.el-table__row:hover) {
    background-color: #f0f9ff !important;
  }
}
.data-id-input {
  :deep(.el-input__wrapper) {
    background: linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%);
    border-radius: 8px;
  }
}
.field-name-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.code-text {
  flex: 1;
  padding: 6px 10px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  font-family: "SF Mono", "Monaco", "Menlo", "Consolas", monospace;
  font-size: 13px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  transition: all 0.2s;
}
.code-text.has-error {
  border-color: #f56c6c;
  background: #fef2f2;
  color: #f56c6c;
}

// 已修改的单元格样式
.cell-dirty :deep(.el-input__wrapper) {
  background: linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%) !important;
  box-shadow: 0 0 0 1px #e6a23c inset, 0 0 4px rgba(230, 162, 60, 0.2) !important;
}
.cell-dirty :deep(.el-input__inner) {
  color: #b88206;
  font-weight: 600;
}
</style>
